import { createClient, isApiError, type SlotUnavailableDetails } from "@colorglo/contracts/client";

/**
 * The staff app's API client.
 *
 * One instance for the whole app. `credentials: "include"` is the only authentication there
 * is — the session is an HttpOnly cookie (P3), so there is no token to attach here and
 * nothing in `localStorage` for a script to find.
 *
 * The types come from `apps/api`'s route graph rather than from a generated file, so a
 * response shape that changes on the server breaks this build in the same commit. P7 wraps
 * these calls in TanStack Query; the client itself stays a plain object.
 */
export const API_URL: string = import.meta.env.VITE_API_URL ?? "http://localhost:3000";

export const api = createClient(API_URL);

/**
 * Slots for a service at one location.
 *
 * This exists as much to prove the type inference as to be used: AGENT_PROMPTS.md P5 asks
 * that the Eden client "gives full type inference in apps/app", and a client that had
 * degraded to `any` would still compile while silently giving up every guarantee the
 * treaty exists for. Here `days` is inferred from the route's return type — no annotation,
 * no cast, no generated schema — so if it ever stops being inferred, this file stops
 * compiling and `bun run typecheck` says which route changed.
 */
export async function fetchAvailability(options: {
  readonly franchise: string;
  readonly serviceIds: readonly string[];
  readonly from: string;
  readonly to: string;
}) {
  const { data, error } = await api.v1
    .franchises({ franchiseId: options.franchise })
    .availability.get({
      query: {
        serviceIds: options.serviceIds.join(","),
        from: options.from,
        to: options.to,
      },
    });

  if (error !== null) return { days: [], error };
  // `data.days` and `data.timezone` are inferred from the handler's return type.
  return { days: data.days, timezone: data.timezone, error: null };
}

/**
 * The three nearby times a 409 offers, or an empty list.
 *
 * docs/API.md makes the conflict path first-class, and this is the client half of it: a
 * booking write that loses the race comes back with `SLOT_UNAVAILABLE` and up to three
 * alternatives, which P7 renders as buttons rather than sending the user back to a
 * calendar.
 */
export function alternativesFrom(error: unknown): readonly string[] {
  if (!isApiError(error)) return [];
  if (error.error.code !== "SLOT_UNAVAILABLE") return [];
  return (error.error.details as SlotUnavailableDetails | undefined)?.alternatives ?? [];
}
